import { useEffect, useRef, useState } from 'react';
import { theme } from '@agentisai/design-system';
import { useSearchParams } from 'react-router';
import { useWorkstreams } from '../queries/useWorkstreams';
import { DeleteWorkstreamDialog } from './DeleteWorkstreamDialog';
import { RailNewButton } from './RailNewButton';
import { RenameWorkstreamDialog } from './RenameWorkstreamDialog';
import { WorkstreamCard } from './WorkstreamCard';

/** URL search-param key for the currently-selected workstream (R3 — URL as truth). */
export const WS_PARAM = 'ws';
/** URL search-param keys for the Files View (S11): `?files=` is the open tab set
 * (comma-separated opaque ids), `?file=` is the active tab. Both are cleared on a
 * workstream switch — they belong to one workstream's ledger. */
export const FILES_PARAM = 'files';
export const FILE_PARAM = 'file';
/** AB-2-2 draft panel: the selected revision, and the open tab (`changes`; absent = the draft). */
export const DRAFT_REV_PARAM = 'rev';
export const DRAFT_VIEW_PARAM = 'view';
/** The open rail panel (R3): absent = Workstreams, `none` = all collapsed. */
export const PANEL_PARAM = 'panel';
/** AB-2-5: a new builder forking a shared agent (its id) — prefills the first message. */
export const FORK_PARAM = 'fork';

/**
 * Point the URL at a workstream (or at none) — the one place that knows which
 * params belong to the workstream being left: its file tabs and draft pointers
 * go; an open Files panel closes on deselect (nothing to list) but every other
 * rail panel stays open.
 */
export function selectWorkstreamParams(prev: URLSearchParams, workstreamId: string | null) {
  const next = new URLSearchParams(prev);
  for (const key of [FILES_PARAM, FILE_PARAM, DRAFT_REV_PARAM, DRAFT_VIEW_PARAM, FORK_PARAM]) {
    next.delete(key);
  }
  if (workstreamId !== null) {
    next.set(WS_PARAM, workstreamId);
  } else {
    next.delete(WS_PARAM);
    if (next.get(PANEL_PARAM) === 'files') next.delete(PANEL_PARAM);
  }
  return next;
}

/**
 * Workstream rail panel body (S-C3). Slots into the `WorkspaceShell` and
 * renders the caller's workstreams as a list of `WorkstreamCard` bubbles
 * sorted newest first (B2 already returns them in that order; we don't
 * re-sort client-side). Active selection is URL state via `useSearchParams`
 * (`?ws=<id>`, frontend-architecture R3) — survives reload, deep-linkable,
 * back-button works.
 *
 * Loading + error states are minimal placeholders for now (Drop 5 only):
 *   - loading → quiet skeleton text
 *   - error   → quiet "couldn't load" line (the QueryCache.onError sink
 *                already forwards to `reportError` for the diagnostic
 *                trail, so this UI is purely user-facing)
 *
 * Empty result → "create your first" affordance that opens the existing
 * CreateWorkstreamPanel via the `onNew` callback (consumer drives the modal
 * state — same pattern as the EmptyState CTA in HomePage today).
 */
export interface WorkstreamsRailBodyProps {
  /** Click handler for the `+ New` subheader action AND the empty-state CTA. */
  readonly onNew: () => void;
}

export function WorkstreamsRailBody({ onNew }: WorkstreamsRailBodyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get(WS_PARAM);
  const query = useWorkstreams();
  // The workstream awaiting delete-confirmation (id + title for the dialog copy);
  // null when no dialog is open. Component-local ephemeral UI state (R1.0).
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);
  // Same shape for the rename dialog (id + current title to prefill the field).
  const [pendingRename, setPendingRename] = useState<{ id: string; title: string } | null>(null);
  // Focus landing after a delete: the deleted card's Delete button unmounts on
  // the refetch, so the dialog's focus-restore (useDialog) would drop focus to
  // <body>. Move it into the rail list body instead (mirrors FilesRailBody).
  const listRef = useRef<HTMLDivElement>(null);

  // S13 — scroll the selected card into view: on selection change AND once the list
  // loads (so a deep-link/refresh into `?ws=` lands scrolled to the selection). The
  // sanctioned imperative-DOM effect (R24/R25 — reads layout, doesn't derive state);
  // `block: 'nearest'` moves only the rail's scroll region, and reduced motion drops
  // the smooth animation. Keyed on `query.isSuccess` so it fires once per successful
  // load rather than on every background refetch — a refetch that errors then recovers
  // flips isSuccess false→true and re-scrolls, which is benign (the selection is still
  // brought into view). The `typeof window` guard mirrors useViewportTier.getMql().
  const activeCardRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!activeId || !activeCardRef.current) return;
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    activeCardRef.current.scrollIntoView({
      block: 'nearest',
      behavior: reduce ? 'auto' : 'smooth',
    });
  }, [activeId, query.isSuccess]);

  function select(id: string) {
    // Replace, not push — selecting a card shouldn't grow the history stack
    // (a back-button press should leave the workstreams view, not unselect).
    // Drops the open file tabs too — they belong to the workstream we're leaving,
    // so they wouldn't resolve in the new one (else the View pane would fetch
    // cross-workstream ids and only recover on a 404).
    setSearchParams((prev) => selectWorkstreamParams(prev, id), { replace: true });
  }

  function deselect() {
    // Drop the whole selection (workstream + any open file tabs) so the workspace
    // falls back to the no-selection EmptyState — no files, no messages, like a
    // fresh login. One replace write (mirrors `select`'s param clearing).
    setSearchParams((prev) => selectWorkstreamParams(prev, null), { replace: true });
  }

  function handleDeleted() {
    // If the deleted workstream was the active one, deselect so the workspace
    // clears to the EmptyState. A non-active delete leaves the current selection
    // intact — the invalidated `listMine` refetch just drops the removed card.
    if (pendingDelete && pendingDelete.id === activeId) deselect();
    setPendingDelete(null);
    // Land focus in the rail body on the next frame (after the dialog's unmount +
    // focus-restore commit) so a keyboard / screen-reader user keeps context.
    requestAnimationFrame(() => listRef.current?.focus());
  }

  if (query.isLoading) {
    return (
      <div
        style={{
          padding: theme.space[4],
          color: theme.color.neutral.faint,
          fontSize: theme.typography.fontSize.bodySm,
        }}
      >
        Loading workstreams…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        style={{
          padding: theme.space[4],
          color: theme.color.neutral.faint,
          fontSize: theme.typography.fontSize.bodySm,
        }}
      >
        Couldn't load workstreams. We'll retry automatically.
      </div>
    );
  }

  const items = query.data?.data ?? [];

  if (items.length === 0) {
    return (
      <div
        style={{
          padding: theme.space[4],
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space[3],
        }}
      >
        <p
          style={{
            margin: 0,
            color: theme.color.neutral.faint,
            fontSize: theme.typography.fontSize.bodySm,
            lineHeight: theme.typography.lineHeight.body,
          }}
        >
          No workstreams yet.
        </p>
        <button
          type="button"
          onClick={onNew}
          style={{
            alignSelf: 'flex-start',
            background: theme.color.neutral.charcoal,
            color: theme.color.neutral.white,
            border: 0,
            padding: `${theme.space[1]} ${theme.space[3]}`,
            borderRadius: theme.radii.sm,
            fontFamily: theme.typography.fontFamily.sans,
            fontSize: theme.typography.fontSize.bodySm,
            fontWeight: theme.typography.fontWeight.semibold,
            cursor: 'pointer',
          }}
        >
          + Create your first workstream
        </button>
      </div>
    );
  }

  return (
    <div ref={listRef} tabIndex={-1} style={{ padding: `0 ${theme.space[4]}`, outline: 'none' }}>
      {items.map((item) => (
        <WorkstreamCard
          key={item.workstreamId}
          ref={activeId === item.workstreamId ? activeCardRef : undefined}
          name={item.title}
          description={item.description ?? ''}
          active={activeId === item.workstreamId}
          onClick={() => select(item.workstreamId)}
          onDelete={() => setPendingDelete({ id: item.workstreamId, title: item.title })}
          onRename={() => setPendingRename({ id: item.workstreamId, title: item.title })}
        />
      ))}
      {pendingRename && (
        // Keyed on the id so opening it for another card remounts with that
        // card's title, not the previous draft.
        <RenameWorkstreamDialog
          key={pendingRename.id}
          workstreamId={pendingRename.id}
          title={pendingRename.title}
          onClose={() => setPendingRename(null)}
        />
      )}
      {pendingDelete && (
        <DeleteWorkstreamDialog
          workstreamId={pendingDelete.id}
          title={pendingDelete.title}
          onClose={() => setPendingDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
WorkstreamsRailBody.displayName = 'WorkstreamsRailBody';

/**
 * The `+ New` subheader action button — extracted so the rail header can host
 * it while the body owns the loading/empty/list states. Same style + tokens as
 * the DS `RailPanel` story's `NewActionButton` (story fixture; not exported).
 */
export interface NewWorkstreamActionProps {
  readonly onClick: () => void;
}

export function NewWorkstreamAction({ onClick }: NewWorkstreamActionProps) {
  return <RailNewButton onClick={onClick}>+ New</RailNewButton>;
}
NewWorkstreamAction.displayName = 'NewWorkstreamAction';
